import { useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  CircularProgress,
  TextField,
  Autocomplete,
} from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { getMomentTz } from "@dexteel/mesf-core";
import type { Moment } from "moment";
import { TaskDateSection } from "../../../shared/components/modals/TaskDateSection";

const moment = getMomentTz();

// ============================================
// Types
// ============================================

interface MoldOption {
  partId: number;
  name: string;
  /** Pre-computed last task timestamp to avoid API call on selection */
  lastTaskTimestamp?: Date | null;
}

interface StrandOption {
  partId: number;
  name: string;
  /** PartId of the mold currently installed on this strand (swap scenario) */
  occupantPartId?: number;
  /** Pre-computed last task timestamp to avoid API call on selection */
  lastTaskTimestamp?: Date | null;
  /** Last task timestamp on this location (when previous part was uninstalled) */
  locationLastTimestamp?: Date | null;
}

interface FormData {
  timestamp: Moment | null;
  selectedMold: MoldOption | null;
  selectedStrand: StrandOption | null;
  operatorNumber: string;
}

interface BaseProps {
  open: boolean;
  isLoading: boolean;
  /** PartId of the mold currently installed on the strand (swap scenario) */
  occupantPartId?: number | null;
  onClose: () => void;
  onConfirm: (
    strandPartId: number,
    moldPartId: number,
    operatorNumber: number,
    timestamp: Date | null,
  ) => void;
}

interface FromStrandProps extends BaseProps {
  /** Right-click on empty strand: strand is fixed, user picks a mold */
  mode: "fromStrand";
  strandName: string;
  strandPartId: number;
  availableMolds: MoldOption[];
  /** Last task timestamp on this strand (when previous mold was uninstalled) */
  locationLastTimestamp?: Date | null;
}

interface FromSparesProps extends BaseProps {
  /** Right-click on offline mold: mold is fixed, user picks a strand */
  mode: "fromSpares";
  moldName: string;
  moldPartId: number;
  availableStrands: StrandOption[];
}

type Props = FromStrandProps | FromSparesProps;

// ============================================
// Component
// ============================================

export const SetMoldOnlineModal = (props: Props) => {
  const {
    open,
    isLoading,
    onClose,
    onConfirm,
    mode,
    occupantPartId: staticOccupantPartId,
  } = props;

  const { control, handleSubmit, reset, watch, trigger } = useForm<FormData>({
    defaultValues: {
      timestamp: null,
      selectedMold: null,
      selectedStrand: null,
      operatorNumber: "",
    },
    mode: "onChange",
  });

  const selectedMold = watch("selectedMold");
  const selectedStrand = watch("selectedStrand");

  // Dynamic occupantPartId: in "fromSpares" mode, derive from selected strand
  const effectiveOccupantPartId =
    mode === "fromSpares"
      ? selectedStrand?.occupantPartId
      : staticOccupantPartId;

  useEffect(() => {
    if (open) {
      reset({
        timestamp: moment(),
        selectedMold: null,
        selectedStrand: null,
        operatorNumber: "",
      });
    }
  }, [open, reset]);

  const onSubmit = (data: FormData) => {
    const operatorNumber = parseInt(data.operatorNumber, 10);
    const ts = data.timestamp?.toDate() ?? null;

    if (mode === "fromStrand") {
      const moldPartId = data.selectedMold?.partId;
      if (!moldPartId) return;
      onConfirm(props.strandPartId, moldPartId, operatorNumber, ts);
    } else {
      const strandPartId = data.selectedStrand?.partId;
      if (!strandPartId) return;
      onConfirm(strandPartId, props.moldPartId, operatorNumber, ts);
    }
  };

  // Resolve partId for task history based on mode
  const taskDatePartId =
    mode === "fromSpares" ? props.moldPartId : (selectedMold?.partId ?? null);

  // Pre-loaded timestamp for the part being installed (known from tree)
  const installedPartLastTimestamp =
    mode === "fromSpares"
      ? selectedStrand?.lastTaskTimestamp
      : selectedMold?.lastTaskTimestamp;

  // Location constraint: last task on the target strand (when previous mold was uninstalled)
  const effectiveLocationLastTimestamp =
    mode === "fromStrand"
      ? props.locationLastTimestamp
      : selectedStrand?.locationLastTimestamp;

  const title = "Set Mold Online - Confirmation";

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
          {/* ── Fixed field (read-only context) ── */}
          {mode === "fromStrand" ? (
            <Box>
              <Typography variant="body2" color="text.secondary">
                Strand
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                {props.strandName}
              </Typography>
            </Box>
          ) : (
            <Box>
              <Typography variant="body2" color="text.secondary">
                Mold
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                {props.moldName}
              </Typography>
            </Box>
          )}

          {/* ── Selector field ── */}
          {mode === "fromStrand" ? (
            <Controller
              name="selectedMold"
              control={control}
              rules={{ required: "Select a mold to install" }}
              render={({ field, fieldState: { error } }) => (
                <Autocomplete
                  {...field}
                  options={props.availableMolds}
                  getOptionLabel={(option) => option.name}
                  isOptionEqualToValue={(option, value) =>
                    option.partId === value.partId
                  }
                  onChange={(_, value) => field.onChange(value)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Select Mold"
                      size="small"
                      error={!!error}
                      helperText={
                        error?.message || "Choose a mold from Mold Spares"
                      }
                    />
                  )}
                  noOptionsText="No molds available in Mold Spares"
                />
              )}
            />
          ) : (
            <Controller
              name="selectedStrand"
              control={control}
              rules={{ required: "Select a strand" }}
              render={({ field, fieldState: { error } }) => (
                <Autocomplete
                  {...field}
                  options={props.availableStrands}
                  getOptionLabel={(option) => option.name}
                  isOptionEqualToValue={(option, value) =>
                    option.partId === value.partId
                  }
                  onChange={(_, value) => field.onChange(value)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Select Strand"
                      size="small"
                      error={!!error}
                      helperText={error?.message || "Choose the target strand"}
                    />
                  )}
                  noOptionsText="No empty strands available"
                />
              )}
            />
          )}

          <Typography variant="body2" color="text.secondary">
            This will install the mold in the selected strand and set it as
            online.
          </Typography>

          {/* Date selection with task history */}
          <TaskDateSection
            control={control}
            trigger={trigger}
            partId={taskDatePartId}
            open={open}
            action="install"
            installedPartLastTimestamp={installedPartLastTimestamp}
            locationLastTimestamp={effectiveLocationLastTimestamp}
            occupantPartId={effectiveOccupantPartId}
            validateAgainstInitialDatetime
          />

          <Controller
            name="operatorNumber"
            control={control}
            rules={{ required: "Operator number is required" }}
            render={({ field, fieldState: { error } }) => (
              <TextField
                {...field}
                label="Operator Number"
                size="small"
                fullWidth
                error={!!error}
                helperText={error?.message || "Enter your Operator Number"}
                inputProps={{ inputMode: "numeric" }}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, "");
                  field.onChange(value);
                }}
              />
            )}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit(onSubmit)}
          variant="contained"
          color="success"
          disabled={isLoading}
          startIcon={isLoading ? <CircularProgress size="1rem" /> : undefined}
        >
          Set Online
        </Button>
      </DialogActions>
    </Dialog>
  );
};
